import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GroupMeChatClient, GroupMeGroupViewModel, GroupMeMessagesViewModel, SendGroupMeMessageToGroupRequest, GroupMeAuthStatusViewModel } from '../api';
import { firstValueFrom } from 'rxjs';
import { KendoAlertService } from '@ticomix/angular-kendo';
import { WindowModule } from '@progress/kendo-angular-dialog';

@Component({
  selector: 'app-groupme-chat',
  templateUrl: './groupme-chat.component.html',
  styleUrls: ['./groupme-chat.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WindowModule
  ]
})
export class GroupMeChatComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer?: ElementRef;
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;

  checkingAuth = true;
  isAuthenticated = false;
  authorizationUrl: string | null = null;
  groups: GroupMeGroupViewModel[] = [];
  selectedGroup: GroupMeGroupViewModel | null = null;
  messages: GroupMeMessagesViewModel | null = null;
  loadingGroups = false;
  loadingMessages = false;
  newMessageText = '';
  sendingMessage = false;
  selectedImageFile: File | null = null;
  private messagePollingInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private groupMeChatClient: GroupMeChatClient,
    private kendoAlert: KendoAlertService
  ) { }

  async ngOnInit() {
    await this.checkAuthStatus();
  }

  ngOnDestroy() {
    this.stopMessagePolling();
  }

  async checkAuthStatus() {
    this.checkingAuth = true;
    try {
      const status = await firstValueFrom(this.groupMeChatClient.getAuthStatus());
      this.isAuthenticated = status.IsUserAuthenticated;
      this.authorizationUrl = status.AuthorizationUrl ?? null;

      if (this.isAuthenticated) {
        await this.loadGroups();
      }
    } catch (error) {
      await this.kendoAlert.alertAsync('Failed to check authentication status.');
    } finally {
      this.checkingAuth = false;
    }
  }

  async loadGroups() {
    this.loadingGroups = true;
    try {
      this.groups = await firstValueFrom(this.groupMeChatClient.getMyGroups());
    } catch (error) {
      await this.kendoAlert.alertAsync('Failed to load GroupMe groups.');
    } finally {
      this.loadingGroups = false;
    }
  }

  async selectGroup(group: GroupMeGroupViewModel) {
    if (this.selectedGroup?.GroupMeId === group.GroupMeId) {
      return;
    }

    this.selectedGroup = group;
    await this.loadMessages();
  }

  async loadMessages() {
    if (!this.selectedGroup?.GroupMeId) {
      return;
    }

    this.loadingMessages = true;
    try {
      this.messages = await firstValueFrom(this.groupMeChatClient.getGroupMessages(this.selectedGroup.GroupMeId));

      if (!this.messages.IsUserAuthenticated) {
        this.isAuthenticated = false;
        this.authorizationUrl = this.messages.AuthorizationUrl ?? null;
        this.stopMessagePolling();
        return;
      }

      if (this.messages.Messages && this.messages.Messages.length > 0) {
        this.startMessagePolling();
      }

      setTimeout(() => this.scrollToBottom(), 100);
    } catch (error) {
      await this.kendoAlert.alertAsync('Failed to load messages.');
    } finally {
      this.loadingMessages = false;
    }
  }

  private startMessagePolling() {
    this.stopMessagePolling();
    this.messagePollingInterval = setInterval(async () => {
      await this.checkForNewMessages();
    }, 60000);
  }

  private stopMessagePolling() {
    if (this.messagePollingInterval) {
      clearInterval(this.messagePollingInterval);
      this.messagePollingInterval = null;
    }
  }

  private async checkForNewMessages() {
    if (!this.selectedGroup?.GroupMeId) {
      return;
    }

    try {
      const currentLastMessageId = this.getLastMessageId();
      const newMessages = await firstValueFrom(this.groupMeChatClient.getGroupMessages(this.selectedGroup.GroupMeId));

      if (!newMessages.IsUserAuthenticated) {
        this.isAuthenticated = false;
        this.authorizationUrl = newMessages.AuthorizationUrl ?? null;
        this.stopMessagePolling();
        return;
      }

      const newLastMessageId = this.getLastMessageIdFromResponse(newMessages);

      if (newLastMessageId !== currentLastMessageId) {
        this.messages = newMessages;
        setTimeout(() => this.scrollToBottom(), 100);
      }
    } catch {
      this.stopMessagePolling();
    }
  }

  private getLastMessageId(): string | null {
    if (!this.messages?.Messages || this.messages.Messages.length === 0) {
      return null;
    }
    return this.messages.Messages[this.messages.Messages.length - 1].Id ?? null;
  }

  private getLastMessageIdFromResponse(response: GroupMeMessagesViewModel): string | null {
    if (!response?.Messages || response.Messages.length === 0) {
      return null;
    }
    return response.Messages[response.Messages.length - 1].Id ?? null;
  }

  async sendMessage() {
    if ((!this.newMessageText.trim() && !this.selectedImageFile) || !this.selectedGroup?.GroupMeId) {
      return;
    }

    this.sendingMessage = true;
    try {
      let imageUrl: string | undefined;

      if (this.selectedImageFile) {
        const uploadResult = await firstValueFrom(this.groupMeChatClient.uploadImage({ data: this.selectedImageFile, fileName: this.selectedImageFile.name }));
        if (!uploadResult.Success || !uploadResult.ImageUrl) {
          if (uploadResult.TokenExpired) {
            await this.kendoAlert.alertAsync('Your GroupMe session has expired. Please reconnect.');
            await this.checkAuthStatus();
            return;
          }
          await this.kendoAlert.alertAsync(uploadResult.ErrorMessage || 'Failed to upload image.');
          return;
        }
        imageUrl = uploadResult.ImageUrl;
      }

      const request = new SendGroupMeMessageToGroupRequest();
      request.GroupId = this.selectedGroup.GroupMeId;
      request.Text = this.newMessageText.trim();
      request.ImageUrl = imageUrl || null;

      const result = await firstValueFrom(this.groupMeChatClient.sendMessage(request));
      if (result.Success) {
        this.newMessageText = '';
        this.removeSelectedImage();
        this.resetTextareaHeight();
        await this.loadMessages();
      } else {
        if (result.TokenExpired) {
          await this.kendoAlert.alertAsync('Your GroupMe session has expired. Please reconnect.');
          await this.checkAuthStatus();
        } else {
          await this.kendoAlert.alertAsync(result.ErrorMessage || 'Failed to send message.');
        }
      }
    } catch {
      await this.kendoAlert.alertAsync('Failed to send message.');
    } finally {
      this.sendingMessage = false;
    }
  }

  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];

    if (!allowedTypes.includes(file.type)) {
      this.kendoAlert.alertAsync('Only PNG, JPEG, and JPG images are allowed.');
      input.value = '';
      return;
    }

    this.selectedImageFile = file;
    input.value = '';
  }

  removeSelectedImage() {
    this.selectedImageFile = null;
  }

  onMessageKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  autoResizeTextarea(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.overflowX = 'hidden';
    textarea.style.overflowY = 'hidden';
    textarea.style.height = 'auto';
    const maxHeight = 150;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
    textarea.style.overflowY = newHeight >= maxHeight ? 'auto' : 'hidden';
  }

  private resetTextareaHeight() {
    if (this.messageInput?.nativeElement) {
      this.messageInput.nativeElement.style.height = 'auto';
    }
  }

  authenticateGroupMe() {
    if (!this.authorizationUrl) {
      return;
    }

    const authWindow = window.open(
      this.authorizationUrl,
      'GroupMe Authentication',
      'width=600,height=700'
    );

    const checkClosed = setInterval(async () => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
        await this.checkAuthStatus();
      }
    }, 500);
  }

  private scrollToBottom() {
    if (this.messagesContainer?.nativeElement) {
      const container = this.messagesContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }
  }
}

